import React, { useState } from "react";
import axios from "../../api/axios";
import {
  Grid,
  Paper,
  Button,
  Typography,
  Snackbar,
  LinearProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";

function ArrearsUpload() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // ✅ Handle file selection
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  // ✅ Upload arrears file
  const handleUpload = async () => {
    if (!file) {
      setSnackbar({ open: true, message: "Please select an Excel file.", severity: "error" });
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    setErrors([]);

    try {
      const res = await axios.post("/arrears/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSnackbar({
        open: true,
        message: `${res.data.message} (${res.data.count} records uploaded)`,
        severity: "success",
      });
      setFile(null);
    } catch (err) {
      if (err.response && err.response.data.errors) {
        setErrors(err.response.data.errors);
        setSnackbar({
          open: true,
          message: "Validation failed. Check error table below.",
          severity: "error",
        });
      } else {
        setSnackbar({
          open: true,
          message: "An unexpected error occurred.",
          severity: "error",
        });
      }
    } finally {
      setUploading(false);
    }
  };

  // ✅ Download arrears upload template
  const downloadTemplate = async () => {
    try {
      const response = await axios.get("/arrears/template/download", {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "arrears_template.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to download template.",
        severity: "error",
      });
    }
  };

  // ✅ Download pay items (Salary Structures + Earnings)
  const downloadPayItems = async () => {
    try {
      const response = await axios.get("/arrears/pay-items/download", {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "pay_items.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to download pay items.",
        severity: "error",
      });
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox py={3}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
          <Typography variant="h5" gutterBottom>
            Upload Arrears
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                style={{ marginTop: "10px" }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpload}
                disabled={uploading}
                sx={{ color: "#fff" }}
              >
                {uploading ? "Uploading..." : "Upload"}
              </Button>
            </Grid>

            <Grid item xs={12} md={6}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={downloadTemplate}
                sx={{ mt: 2 }}
              >
                Download Template
              </Button>
            </Grid>

            <Grid item xs={12} md={6}>
              <Button variant="outlined" color="success" onClick={downloadPayItems} sx={{ mt: 2 }}>
                Download Pay Items
              </Button>
            </Grid>
          </Grid>

          {uploading && (
            <MDBox mt={3}>
              <LinearProgress />
              <Typography variant="body2" sx={{ mt: 1 }}>
                Uploading, please wait...
              </Typography>
            </MDBox>
          )}
        </Paper>

        {/* ✅ Error Table */}
        {errors.length > 0 && (
          <Paper elevation={3} sx={{ p: 3, mt: 3, borderRadius: 3 }}>
            <Typography variant="h6" color="error" gutterBottom>
              Validation Errors
            </Typography>

            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8d7da" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>Row</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Issue</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Expected</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Provided</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errors.map((error, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{error.Row}</TableCell>
                    <TableCell>{error.Issue}</TableCell>
                    <TableCell>{error.Expected || "-"}</TableCell>
                    <TableCell>{error.Provided || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </MDBox>

      {/* Snackbar for messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default ArrearsUpload;
